import { IconCheck } from "@dream/ui";
import { Link } from "@tanstack/react-router";
import { ClientMark } from "./client-mark.tsx";

/**
 * Lista “Meus calendários” da sidebar Ofative.
 *
 * Cada cliente é um calendário. O clique isola aquele cliente na grade.
 * “Atribuídas a mim” recorta só o que está com o operador — combina com o
 * cliente. “+ Adicionar conta” abre as integrações do postador.
 */

export interface CalendarListClient {
  id: string;
  name: string;
  color: string;
  logoUrl?: string | null;
  hasLogo?: boolean;
}

export interface CalendarListMyTasks {
  active: boolean;
  initials: string;
  onToggle: () => void;
}

export function CalendarList({
  clients,
  activeIds,
  onToggle,
  myTasks,
}: {
  clients: CalendarListClient[];
  activeIds: string[];
  onToggle: (clientId: string) => void;
  myTasks?: CalendarListMyTasks | null;
}) {
  return (
    <div className="px-2 pb-3" data-testid="my-calendars">
      <p className="px-2.5 pb-1 pt-2 text-[14px] font-semibold text-[var(--color-ink-muted)]">
        Meus calendários
      </p>
      <ul className="space-y-0.5">
        {myTasks !== null && myTasks !== undefined && (
          <li>
            <button
              type="button"
              data-testid="my-tasks-calendar"
              onClick={myTasks.onToggle}
              aria-pressed={myTasks.active}
              className={[
                "flex w-full items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-left text-[14px]",
                "transition-[background-color,color,box-shadow,opacity,transform] duration-[var(--dream-duration-fast)] ease-[var(--dream-ease-standard)]",
                myTasks.active
                  ? "bg-[var(--color-surface)] font-semibold text-[var(--color-ink)] shadow-[var(--shadow-xs)]"
                  : "font-medium text-[var(--color-ink-secondary)] hover:bg-[var(--color-surface)]/80 hover:text-[var(--color-ink)]",
              ].join(" ")}
            >
              <span className="relative flex size-6 shrink-0 items-center justify-center">
                <span
                  aria-hidden="true"
                  className="flex size-5 items-center justify-center rounded-full bg-brand-50 text-[0.5625rem] font-bold text-brand-600"
                >
                  {myTasks.initials}
                </span>
                {myTasks.active && (
                  <span
                    aria-hidden="true"
                    className="absolute -bottom-0.5 -right-0.5 flex size-3 items-center justify-center rounded-full bg-[#0074FF] text-white ring-1 ring-[var(--color-surface)]"
                  >
                    <IconCheck className="size-2" />
                  </span>
                )}
              </span>
              <span className="min-w-0 truncate">Atribuídas a mim</span>
            </button>
          </li>
        )}
        {clients.length === 0 ? (
          <li>
            <p className="px-2.5 py-1 text-[12px] text-[var(--color-ink-muted)]">
              Cadastre um cliente para aparecer aqui.
            </p>
          </li>
        ) : (
          clients.map((client) => {
            const exclusive = activeIds.length === 1 && activeIds[0] === client.id;
            const dimmed = activeIds.length > 0 && !activeIds.includes(client.id);
            return (
              <li key={client.id}>
                <button
                  type="button"
                  onClick={() => onToggle(client.id)}
                  aria-pressed={exclusive}
                  className={[
                    "flex w-full items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-left text-[14px]",
                    "transition-[background-color,color,box-shadow,opacity,transform] duration-[var(--dream-duration-fast)] ease-[var(--dream-ease-standard)]",
                    exclusive
                      ? "bg-[var(--color-surface)] font-semibold text-[var(--color-ink)] shadow-[var(--shadow-xs)]"
                      : "font-medium text-[var(--color-ink-secondary)] hover:bg-[var(--color-surface)]/80 hover:text-[var(--color-ink)]",
                    dimmed ? "opacity-50" : "",
                  ].join(" ")}
                >
                  <span className="relative flex size-6 shrink-0 items-center justify-center">
                    <ClientMark
                      color={client.color}
                      logoUrl={client.logoUrl}
                      name={client.name}
                      size={20}
                    />
                    {(activeIds.length === 0 || exclusive || activeIds.includes(client.id)) && (
                      <span
                        aria-hidden="true"
                        className="absolute -bottom-0.5 -right-0.5 flex size-3 items-center justify-center rounded-full bg-[#0074FF] text-white ring-1 ring-[var(--color-surface)]"
                      >
                        <IconCheck className="size-2" />
                      </span>
                    )}
                  </span>
                  <span className="min-w-0 truncate">{client.name}</span>
                </button>
              </li>
            );
          })
        )}
      </ul>
      <Link
        to="/publicacoes"
        className="mt-1 flex items-center px-2.5 py-1.5 text-[13px] font-semibold text-[#0074FF] transition-colors duration-[var(--dream-duration-fast)] hover:text-[var(--dream-brand-hover)] hover:underline"
      >
        + Adicionar conta
      </Link>
    </div>
  );
}
